<template>
  <div class="sso-setup pa-2 w-100">
    <Typography variant="subtitle-1" class="mb-2">SSO Setup</Typography>
    <Typography variant="body-2" class="mb-5">
      Create an SSO setup link for this client's IT administrator. The link is not emailed
      automatically. Copy it and send it to the approved recipient.
    </Typography>

    <v-alert v-if="!hasClientId" type="warning" text dense class="mb-4">
      Select a client before creating an SSO setup ticket.
    </v-alert>

    <v-alert
      v-if="safeError && !isSetupDisabled"
      type="error"
      text
      dense
      dismissible
      class="mb-4"
      data-testid="sso-error"
      @input="safeError = null"
    >
      {{ safeError.message }}
      <span v-if="safeError.errorCode"> (Error code: {{ safeError.errorCode }})</span>
    </v-alert>

    <v-alert
      v-if="operationStatusMessage && !isSetupDisabled"
      type="info"
      text
      dense
      class="mb-4"
      data-testid="sso-operation-status"
    >
      {{ operationStatusMessage }}
    </v-alert>

    <div
      v-if="hasClientId && !isClientReady"
      class="d-flex align-center mb-4"
      data-testid="sso-client-loading"
    >
      <v-progress-circular indeterminate size="20" width="2" color="brandPrimary" class="mr-3" />
      <Typography variant="body-2">Loading client…</Typography>
    </div>

    <div
      v-else-if="isLoadingSetup"
      class="d-flex justify-center my-6"
      data-testid="sso-setup-loading"
    >
      <v-progress-circular indeterminate color="brandPrimary" size="40" />
    </div>

    <v-alert
      v-else-if="isSetupDisabled"
      type="info"
      text
      dense
      class="mb-4"
      data-testid="sso-setup-disabled"
    >
      {{ safeError.message }}
    </v-alert>

    <section
      v-else-if="isManualEnablementFormOpen && canOpenManualEnablement"
      class="sso-ticket-created"
      data-testid="sso-manual-enablement-form"
    >
      <Typography variant="subtitle-2" class="mb-3">Record manual SSO enablement</Typography>
      <Typography variant="body-2" class="mb-4">Confirm that Launch Team has:</Typography>

      <v-alert
        v-if="manualEnablementError && !isManualEnablementConfirmationOpen"
        type="error"
        text
        dense
        class="mb-4"
      >
        {{ manualEnablementError.message }}
        <span v-if="manualEnablementError.errorCode">
          (Error code: {{ manualEnablementError.errorCode }})
        </span>
      </v-alert>

      <v-checkbox
        v-for="requirement in manualEnablementItems"
        :key="requirement.id"
        v-model="acknowledgedRequirementIds"
        :label="requirement.label"
        :value="requirement.id"
        :disabled="isManualEnablementPending || isManualEnablementOutcomeUnknown"
        hide-details
        class="mt-0 mb-2"
      />

      <v-text-field
        v-model.trim="manualActivationReference"
        label="Activation reference"
        outlined
        dense
        counter
        :maxlength="ACTIVATION_REFERENCE_MAX_LENGTH"
        :rules="activationReferenceRules"
        :disabled="isManualEnablementPending || isManualEnablementOutcomeUnknown"
        class="mt-5"
      />

      <Typography variant="subtitle-2" class="mb-2">Client login settings</Typography>
      <Typography
        v-if="setup.auth0ConnectionName"
        variant="body-2"
        class="mb-4"
        data-testid="sso-connection-name"
      >
        <span class="font-weight-bold">SSO connection:</span>
        <code class="sso-ticket-url">{{ setup.auth0ConnectionName }}</code>
      </Typography>

      <v-text-field
        v-model="ssoButtonLabel"
        label="Button label"
        outlined
        dense
        counter
        :maxlength="SSO_BUTTON_LABEL_MAX_LENGTH"
        :rules="ssoButtonLabelRules"
        :disabled="isManualEnablementPending || isManualEnablementOutcomeUnknown"
        data-testid="sso-button-label"
      />

      <v-alert
        v-if="hasClientSSOProviderConflict"
        type="warning"
        text
        dense
        class="mb-4"
        data-testid="sso-provider-conflict"
      >
        {{ CLIENT_SSO_CONFIGURED_MESSAGE }}
      </v-alert>
      <v-alert v-else type="warning" text dense class="mb-4" data-testid="sso-enable-warning">
        {{ ENABLE_SSO_WARNING }}
      </v-alert>

      <div class="d-flex align-center justify-end">
        <Button text :disabled="isManualEnablementPending" @click="cancelManualEnablementForm">
          Cancel
        </Button>
        <Button
          color="brandPrimary"
          :disabled="!canSubmitManualEnablement"
          @click="openManualEnablementConfirmation"
        >
          Record manual enablement
        </Button>
      </div>
    </section>

    <section v-else-if="isTerminalTicket" class="sso-ticket-created">
      <div class="d-flex align-center mb-4">
        <v-chip color="warning" label small class="mr-3">{{ ticketStatusLabel }}</v-chip>
      </div>

      <Typography v-if="setup.clientAdminEmail" variant="body-2" class="mb-2">
        <span class="font-weight-bold">Approved recipient:</span>
        {{ setup.clientAdminEmail }}
      </Typography>
      <Typography v-if="isExpiredTicket" variant="body-2" class="mb-5">
        The setup ticket expired on {{ formattedTicketExpiry }}.
      </Typography>
      <Typography v-else variant="body-2" class="mb-5"> The setup ticket was revoked. </Typography>

      <div class="d-flex align-center flex-wrap">
        <Button
          v-if="canGenerateReplacement"
          color="brandPrimary"
          class="mr-4"
          @click="openTicketActionDialog('REGENERATE_TICKET')"
        >
          Generate replacement ticket
        </Button>
        <Button v-if="canOpenManualEnablement" text @click="openManualEnablementForm">
          Record manual SSO enablement
        </Button>
      </div>
    </section>

    <section v-else-if="isTicketCreated" class="sso-ticket-created">
      <div class="d-flex align-center mb-4">
        <v-chip color="success" label small class="mr-3">Ticket created</v-chip>
        <Typography v-if="isFreshTicketUrl" variant="body-2">
          Copy this link and send it to
          <span class="font-weight-bold">{{ setup.clientAdminEmail }}</span>
        </Typography>
      </div>

      <Typography v-if="!isFreshTicketUrl" variant="body-2" class="mb-2">
        <span class="font-weight-bold">Approved recipient:</span>
        {{ setup.clientAdminEmail }}
      </Typography>
      <Typography variant="body-2" class="mb-4">
        <span class="font-weight-bold">Expires:</span> {{ formattedTicketExpiry }}
      </Typography>

      <v-sheet v-if="transientTicketUrl" outlined rounded class="sso-sensitive-link pa-4 mb-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <Typography variant="subtitle-2">Setup link</Typography>
          <div class="d-flex align-center">
            <v-tooltip bottom>
              <!-- Button only re-emits click, so the span receives the tooltip's hover/focus events. -->
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <Button icon small aria-label="Copy setup link" @click="copyTicketUrl">
                    <v-icon>mdi-content-copy</v-icon>
                  </Button>
                </span>
              </template>
              <span>Copy setup link</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <Button
                    icon
                    small
                    aria-label="Dismiss setup link"
                    :disabled="!isTicketUrlCopied"
                    @click="dismissTicketUrl"
                  >
                    <v-icon>mdi-close</v-icon>
                  </Button>
                </span>
              </template>
              <span>Dismiss setup link</span>
            </v-tooltip>
          </div>
        </div>
        <code data-testid="sso-ticket-url" class="sso-ticket-url">{{ transientTicketUrl }}</code>
        <p class="text-caption mt-2 mb-0" data-testid="sso-link-memory-notice">
          This link is shown only while you stay on this page. If you leave, refresh, or switch
          client, you'll need to regenerate the ticket to get a new link.
        </p>
        <p
          v-if="isCopyFailed"
          class="error--text text-caption mt-2 mb-0"
          role="alert"
          data-testid="sso-copy-error"
        >
          Could not copy the link. Select it and copy it manually.
        </p>
      </v-sheet>
      <Typography v-else variant="body-2" class="mb-5">
        Setup link was issued. It is not available to view again.
      </Typography>

      <div class="d-flex align-center justify-space-between sso-ticket-actions">
        <Button
          v-if="canGenerateReplacement"
          color="brandPrimary"
          @click="openTicketActionDialog('REGENERATE_TICKET')"
        >
          Regenerate ticket
        </Button>
        <Button
          v-if="canRevoke"
          color="error"
          text
          @click="openTicketActionDialog('REVOKE_TICKET')"
        >
          Revoke ticket
        </Button>
      </div>
      <Button
        v-if="canOpenManualEnablement && !isFreshTicketUrl"
        text
        class="mt-5"
        @click="openManualEnablementForm"
      >
        Record manual SSO enablement
      </Button>
    </section>

    <section v-else-if="isManuallyEnabledTicket" class="sso-ticket-created">
      <div class="d-flex align-center mb-4">
        <v-chip color="success" label small>Manually enabled</v-chip>
      </div>
      <Typography variant="body-2" class="mb-3">
        Launch Team completed manual SSO validation and enablement. SSO login is on for this client.
      </Typography>
      <Typography variant="body-2" class="mb-2">
        <span class="font-weight-bold">Recorded:</span> {{ formattedManualEnablementTime }}
      </Typography>
      <Typography variant="body-2" class="mb-3">
        <span class="font-weight-bold">Reference:</span>
        {{ setup.manualActivationReference || "Not available" }}
      </Typography>
      <Typography variant="body-2">
        Ticket actions are no longer available for this setup.
      </Typography>
    </section>

    <v-alert
      v-else-if="isClientSSOConfigured"
      type="info"
      text
      dense
      class="mb-4"
      data-testid="sso-client-sso-configured"
    >
      {{ CLIENT_SSO_CONFIGURED_MESSAGE }}
    </v-alert>

    <v-form
      v-else-if="canShowCreateForm"
      ref="form"
      v-model="formIsValid"
      class="sso-setup-form"
      @submit.prevent="submit"
    >
      <v-alert v-if="isIncompleteSetup" type="info" text dense class="mb-4">
        A previous setup attempt did not complete. Create the setup ticket again.
      </v-alert>

      <v-text-field
        v-model.trim="clientAdminEmail"
        label="Client IT administrator email"
        type="email"
        outlined
        dense
        hide-details="auto"
        :disabled="!isClientReady || isCreatingSetup || isCreateOutcomeUnknown"
        :rules="emailRules"
        class="mb-4"
        @input="clearConfirmationValidation"
      />

      <v-text-field
        v-model.trim="clientAdminEmailConfirmation"
        label="Confirm email"
        type="email"
        outlined
        dense
        hide-details="auto"
        :disabled="!isClientReady || isCreatingSetup || isCreateOutcomeUnknown"
        :rules="confirmationRules"
        class="mb-5"
      />

      <Typography
        v-if="isCreateOutcomeUnknown"
        variant="body-2"
        class="mb-4"
        data-testid="sso-create-outcome-unknown"
      >
        The result is not confirmed. Retry to confirm it, or Cancel to refresh the setup.
      </Typography>

      <div class="d-flex align-center">
        <Button
          color="brandPrimary"
          :loading="isCreatingSetup"
          :disabled="!canCreate"
          type="submit"
        >
          Create setup ticket
        </Button>
        <Button
          v-if="isCreateOutcomeUnknown"
          text
          class="ml-4"
          :disabled="isCreatingSetup"
          @click="cancelUnknownCreate"
        >
          Cancel
        </Button>
      </div>
    </v-form>

    <div v-else-if="isCreateInProgress" data-testid="sso-create-in-progress">
      <Button color="brandPrimary" @click="loadSetup()">Check again</Button>
    </div>

    <div v-else-if="setupLoadFailed">
      <Button color="brandPrimary" @click="loadSetup()">Try again</Button>
    </div>

    <v-dialog v-model="isTicketActionDialogOpen" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ ticketActionDialogTitle }}</v-card-title>
        <v-card-text>
          <Typography variant="body-2" class="mb-5">
            {{ ticketActionDialogDescription }}
          </Typography>

          <v-alert
            v-if="actionError"
            type="error"
            text
            dense
            class="mb-4"
            data-testid="sso-action-error"
          >
            {{ actionError.message }}
            <span v-if="actionError.errorCode"> (Error code: {{ actionError.errorCode }})</span>
          </v-alert>

          <v-select
            v-model="reasonCode"
            :items="reasonOptions"
            item-text="label"
            item-value="code"
            label="Reason"
            outlined
            dense
            :disabled="isTicketActionPending || isTicketActionOutcomeUnknown"
            class="mb-3"
          />

          <v-text-field
            v-model.trim="reasonNote"
            :label="
              isReasonNoteRequired ? 'Additional note (required)' : 'Additional note (optional)'
            "
            outlined
            dense
            counter
            :maxlength="REASON_NOTE_MAX_LENGTH"
            :rules="reasonNoteRules"
            :disabled="isTicketActionPending || isTicketActionOutcomeUnknown"
            data-testid="sso-reason-note"
          />
          <Typography
            v-if="isTicketActionOutcomeUnknown"
            variant="body-2"
            data-testid="sso-action-outcome-unknown"
          >
            The result is not confirmed. Retry to confirm it, or Cancel to refresh the setup.
          </Typography>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <Button text :disabled="isTicketActionPending" @click="cancelTicketActionDialog">
            Cancel
          </Button>
          <Button
            :color="ticketAction === 'REVOKE_TICKET' ? 'error' : 'brandPrimary'"
            :loading="isTicketActionPending"
            :disabled="!canConfirmTicketAction"
            @click="confirmTicketAction"
          >
            {{ ticketActionDialogConfirmLabel }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isManualEnablementConfirmationOpen" max-width="560" persistent>
      <v-card>
        <v-card-title>Record manual SSO enablement</v-card-title>
        <v-card-text>
          <v-alert
            v-if="manualEnablementError"
            type="error"
            text
            dense
            class="mb-4"
            data-testid="sso-manual-enablement-error"
          >
            {{ manualEnablementError.message }}
            <span v-if="manualEnablementError.errorCode">
              (Error code: {{ manualEnablementError.errorCode }})
            </span>
          </v-alert>
          <Typography variant="body-2">
            This records the completed Launch Team handoff and closes ticket management for this
            setup. {{ ENABLE_SSO_WARNING }}
          </Typography>
          <Typography v-if="isManualEnablementOutcomeUnknown" variant="body-2" class="mt-3">
            The result is not confirmed. Retry to confirm it, or Cancel to refresh the setup.
          </Typography>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <Button
            text
            :disabled="isManualEnablementPending"
            @click="cancelManualEnablementConfirmation"
          >
            Cancel
          </Button>
          <Button
            color="brandPrimary"
            :loading="isManualEnablementPending"
            :disabled="!canSubmitManualEnablement"
            @click="confirmManualEnablement"
          >
            Enable SSO for client
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorDialogV2
      v-model="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-button-text="confirmDialog.confirmButtonText"
      :cancel-button-text="confirmDialog.cancelButtonText"
      :show-cancel-button="true"
      @confirm="resolveConfirmDialog(true)"
      @cancel="resolveConfirmDialog(false)"
    />
  </div>
</template>

<script>
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import AuthService from "@/services/AuthService";
import { formRules } from "@/shared_data/data.js";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { mapState } from "vuex";

const EMAIL_MAX_LENGTH = 254;
const REASON_NOTE_MAX_LENGTH = 255;
const ACTIVATION_REFERENCE_MAX_LENGTH = 255;
const SSO_BUTTON_LABEL_MAX_LENGTH = 255;
const DEFAULT_SSO_BUTTON_LABEL = "Login with SSO";
// Mirrors the backend's accepted activation reference format.
const ACTIVATION_REFERENCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ._:/-]*$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const CLIENT_SSO_CONFIGURED_MESSAGE =
  "This client already has SSO configured. SSO setup is not available for this client.";
const ENABLE_SSO_WARNING =
  "This turns on SSO login for this client's users immediately. Their login page will show the SSO button.";
// Browsers fire setTimeout early when the delay exceeds a 32-bit millisecond count.
const MAX_TIMER_DELAY_MS = 2147483647;

// Buttons use the same rules as their fields, so the two cannot disagree.
const passesRules = (rules, value) => rules.every((rule) => rule(value) === true);

// Used only when the response has no message, e.g. a network failure.
const FALLBACK_ERROR_MESSAGE = "Something went wrong with SSO setup. Try again later.";
const NETWORK_ERROR_MESSAGE =
  "Could not reach the SSO setup service. Check your connection and try again.";

// The backend returns operationStatus without message text.
const OPERATION_STATUS_MESSAGES = {
  IN_PROGRESS: "The previous request is still processing. Refresh shortly to see the result.",
  SUPPORT_RECOVERY: "This SSO setup needs support review before it can continue.",
};

// Configuration checks run before any change, so these 503s are a definite "nothing happened".
// A failed client update leaves the setup open, and its key replays the failure, so retry with a new key.
const DEFINITE_SERVER_ERROR_CODES = [
  "SSO_SETUP_DISABLED",
  "SSO_SETUP_CONFIGURATION_INVALID",
  "SSO_SETUP_CLIENT_UPDATE_FAILED",
];

const isOutcomeUnknown = (failure) =>
  !failure.status || (failure.status >= 500 && !DEFINITE_SERVER_ERROR_CODES.includes(failure.code));

export default {
  name: "SSOSetup",
  components: {
    Button,
    ErrorDialogV2,
    Typography,
  },
  props: {
    client: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      clientAdminEmail: "",
      clientAdminEmailConfirmation: "",
      formIsValid: false,
      setup: null,
      allowedActions: [],
      allowedReasons: {},
      manualEnablementRequirements: null,
      // Returned by GET only; mutation results leave it unchanged.
      clientSSOProvider: null,
      operationStatus: "",
      transientTicketUrl: "",
      isTicketUrlCopied: false,
      isCopyFailed: false,
      ticketUrlIssuance: "",
      isLoadingSetup: false,
      setupLoadFailed: false,
      isCreatingSetup: false,
      isCreateOutcomeUnknown: false,
      safeError: null,
      pendingRequest: null,
      setupRequestId: 0,
      // Changes only on client switch; reloads change setupRequestId but must not cancel mutations.
      clientEpoch: 0,
      ticketExpiryTimer: null,
      isTicketActionDialogOpen: false,
      ticketAction: "",
      reasonCode: "",
      reasonNote: "",
      isTicketActionPending: false,
      isTicketActionOutcomeUnknown: false,
      actionError: null,
      isManualEnablementFormOpen: false,
      isManualEnablementConfirmationOpen: false,
      acknowledgedRequirementIds: [],
      manualActivationReference: "",
      ssoButtonLabel: DEFAULT_SSO_BUTTON_LABEL,
      isManualEnablementPending: false,
      isManualEnablementOutcomeUnknown: false,
      manualEnablementError: null,
      confirmDialog: {
        show: false,
        title: "",
        message: "",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
      },
      confirmDialogResolve: null,
      REASON_NOTE_MAX_LENGTH,
      ACTIVATION_REFERENCE_MAX_LENGTH,
      SSO_BUTTON_LABEL_MAX_LENGTH,
      CLIENT_SSO_CONFIGURED_MESSAGE,
      ENABLE_SSO_WARNING,
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    hasClientId() {
      return Boolean(this.client && this.client.clientId);
    },
    // Account Management falls back to the user's own client while the selected one loads.
    isClientReady() {
      return (
        this.hasClientId &&
        this.globalClientId != null &&
        Number(this.client.clientId) === Number(this.globalClientId)
      );
    },
    readyClientId() {
      return this.isClientReady ? this.client.clientId : null;
    },
    setupStatus() {
      return (this.setup && this.setup.status) || "";
    },
    isTicketCreated() {
      return this.setupStatus === "TICKET_CREATED";
    },
    activeTicketExpiresAt() {
      return (this.isTicketCreated && this.setup.ticketExpiresAt) || "";
    },
    // NOT_STARTED with IN_PROGRESS is a create still running; only a finished one is incomplete.
    isCreateInProgress() {
      return this.setupStatus === "NOT_STARTED" && this.operationStatus === "IN_PROGRESS";
    },
    isIncompleteSetup() {
      return this.setupStatus === "NOT_STARTED" && !this.isCreateInProgress;
    },
    isExpiredTicket() {
      return this.setupStatus === "EXPIRED";
    },
    isTerminalTicket() {
      return ["REVOKED", "EXPIRED"].includes(this.setupStatus);
    },
    isManuallyEnabledTicket() {
      return this.setupStatus === "MANUALLY_ENABLED";
    },
    canShowCreateForm() {
      return (
        this.isClientReady &&
        !this.setupLoadFailed &&
        !this.clientSSOProvider &&
        (!this.setup || this.isIncompleteSetup) &&
        (!this.operationStatus || this.isIncompleteSetup)
      );
    },
    // A client that already has SSO can never be issued a setup ticket.
    isClientSSOConfigured() {
      return (
        this.isClientReady &&
        !this.setupLoadFailed &&
        Boolean(this.clientSSOProvider) &&
        (!this.setup || this.isIncompleteSetup) &&
        (!this.operationStatus || this.isIncompleteSetup)
      );
    },
    // An equal provider means an earlier attempt already set it, so enablement can finish.
    hasClientSSOProviderConflict() {
      return (
        Boolean(this.clientSSOProvider) &&
        Boolean(this.setup && this.setup.auth0ConnectionName) &&
        this.clientSSOProvider !== this.setup.auth0ConnectionName
      );
    },
    operationStatusMessage() {
      if (this.isIncompleteSetup) return "";
      return OPERATION_STATUS_MESSAGES[this.operationStatus] || "";
    },
    // The backend feature flag is off or unreachable; retrying cannot help.
    isSetupDisabled() {
      return Boolean(
        this.safeError &&
          this.safeError.status === 503 &&
          this.safeError.code === "SSO_SETUP_DISABLED"
      );
    },
    canRevoke() {
      return !this.operationStatus && this.allowedActions.includes("REVOKE_TICKET");
    },
    canGenerateReplacement() {
      return !this.operationStatus && this.allowedActions.includes("REGENERATE_TICKET");
    },
    canOpenManualEnablement() {
      return (
        !this.operationStatus &&
        this.allowedActions.includes("MARK_MANUALLY_ENABLED") &&
        this.manualEnablementItems.length > 0
      );
    },
    // The link from create/regenerate before it has been copied (Screen 2).
    isFreshTicketUrl() {
      return Boolean(this.transientTicketUrl) && !this.isTicketUrlCopied;
    },
    // A create or regenerate response would be dropped once this component is gone.
    isLinkAtRisk() {
      return (
        this.isFreshTicketUrl ||
        this.isCreatingSetup ||
        (this.isTicketActionPending && this.ticketAction === "REGENERATE_TICKET")
      );
    },
    ticketStatusLabel() {
      return this.isExpiredTicket ? "Ticket expired" : "Ticket revoked";
    },
    formattedTicketExpiry() {
      if (!this.setup || !this.setup.ticketExpiresAt) return "Not available";
      return moment.utc(this.setup.ticketExpiresAt).format("MMM D, YYYY, HH:mm [UTC]");
    },
    formattedManualEnablementTime() {
      if (!this.setup || !this.setup.manuallyEnabledAt) return "Not available";
      return moment.utc(this.setup.manuallyEnabledAt).format("MMM D, YYYY, HH:mm [UTC]");
    },
    normalizedClientAdminEmail() {
      return this.clientAdminEmail.trim().toLowerCase();
    },
    normalizedClientAdminEmailConfirmation() {
      return this.clientAdminEmailConfirmation.trim().toLowerCase();
    },
    emailsMatch() {
      return (
        Boolean(this.normalizedClientAdminEmail) &&
        this.normalizedClientAdminEmail === this.normalizedClientAdminEmailConfirmation
      );
    },
    emailRules() {
      return formRules.emailRequired(EMAIL_MAX_LENGTH);
    },
    confirmationRules() {
      return [
        (value) => Boolean(value) || "Confirm the client IT administrator email",
        () => this.emailsMatch || "Email addresses must match",
      ];
    },
    canCreate() {
      return (
        this.isClientReady &&
        !this.isCreatingSetup &&
        passesRules(this.emailRules, this.clientAdminEmail.trim()) &&
        passesRules(this.confirmationRules, this.clientAdminEmailConfirmation.trim())
      );
    },
    reasonOptions() {
      return this.allowedReasons[this.ticketAction] || [];
    },
    isReasonNoteRequired() {
      const reason = this.reasonOptions.find((option) => option.code === this.reasonCode);
      return Boolean(reason && reason.noteRequired === true);
    },
    reasonNoteRules() {
      return this.isReasonNoteRequired
        ? formRules.stringRequired(REASON_NOTE_MAX_LENGTH, "note")
        : formRules.stringOptional(REASON_NOTE_MAX_LENGTH, "note");
    },
    canConfirmTicketAction() {
      if (this.isTicketActionPending) return false;
      if (!this.reasonOptions.some((reason) => reason.code === this.reasonCode)) return false;
      return passesRules(this.reasonNoteRules, this.reasonNote.trim());
    },
    ticketActionDialogTitle() {
      return this.ticketAction === "REVOKE_TICKET"
        ? "Revoke setup ticket"
        : "Regenerate setup ticket";
    },
    ticketActionDialogDescription() {
      return this.ticketAction === "REVOKE_TICKET"
        ? "The client will no longer be able to open this setup link. This does not delete any Auth0 connection."
        : "The current setup link will be revoked before a replacement is created.";
    },
    ticketActionDialogConfirmLabel() {
      return this.ticketAction === "REVOKE_TICKET" ? "Revoke ticket" : "Regenerate ticket";
    },
    manualEnablementItems() {
      const requirements = this.manualEnablementRequirements;
      return (requirements && Array.isArray(requirements.items) && requirements.items) || [];
    },
    activationReferenceRules() {
      return [
        ...formRules.stringRequired(ACTIVATION_REFERENCE_MAX_LENGTH, "activation reference"),
        (value) =>
          !value ||
          ACTIVATION_REFERENCE_PATTERN.test(value) ||
          "Use letters, numbers, spaces, and . _ : / - only",
        (value) => !value || !value.includes("://") || "Enter a change reference, not a link",
      ];
    },
    isActivationReferenceValid() {
      return passesRules(this.activationReferenceRules, this.manualActivationReference.trim());
    },
    ssoButtonLabelRules() {
      return [
        ...formRules.stringOptional(SSO_BUTTON_LABEL_MAX_LENGTH, "button label"),
        (value) => !value || !CONTROL_CHARACTER_PATTERN.test(value) || "Use plain text only",
      ];
    },
    canSubmitManualEnablement() {
      return (
        !this.isManualEnablementPending &&
        !this.hasClientSSOProviderConflict &&
        this.manualEnablementItems.length > 0 &&
        this.manualEnablementItems
          .filter((requirement) => requirement.required)
          .every((requirement) => this.acknowledgedRequirementIds.includes(requirement.id)) &&
        this.isActivationReferenceValid &&
        passesRules(this.ssoButtonLabelRules, this.ssoButtonLabel.trim())
      );
    },
  },
  watch: {
    // Single trigger, so a client switch updating the prop and global client loads only once.
    readyClientId: {
      immediate: true,
      handler(clientId, previousClientId) {
        if (previousClientId !== undefined) {
          this.resetForm();
          this.clearSetupState();
        }
        if (clientId != null) this.loadSetup();
      },
    },
    activeTicketExpiresAt: {
      immediate: true,
      handler: "scheduleTicketExpiry",
    },
    "confirmDialog.show"(open) {
      // Closing the dialog without choosing counts as "stay".
      if (!open && this.confirmDialogResolve) this.resolveConfirmDialog(false);
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.warnBeforeUnload);
  },
  beforeDestroy() {
    // Treated like a client switch, so a late response is ignored.
    this.clientEpoch += 1;
    window.removeEventListener("beforeunload", this.warnBeforeUnload);
    this.clearTicketExpiryTimer();
    this.transientTicketUrl = "";
  },
  methods: {
    clearConfirmationValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    resetForm() {
      this.clientAdminEmail = "";
      this.clientAdminEmailConfirmation = "";
      this.formIsValid = false;
      this.isCreateOutcomeUnknown = false;
      this.pendingRequest = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    cancelUnknownCreate() {
      this.resetForm();
      this.safeError = null;
      this.loadSetup();
    },
    clearSetupState() {
      this.clientEpoch += 1;
      this.setupRequestId += 1;
      this.setup = null;
      this.allowedActions = [];
      this.allowedReasons = {};
      this.manualEnablementRequirements = null;
      this.clientSSOProvider = null;
      this.operationStatus = "";
      this.setTicketUrl("");
      this.isLoadingSetup = false;
      this.setupLoadFailed = false;
      this.isCreatingSetup = false;
      this.isTicketActionPending = false;
      this.isManualEnablementPending = false;
      this.safeError = null;
      this.closeTicketActionDialog();
      this.closeManualEnablementForm();
    },
    applySetupResult(result, { keepError = false } = {}) {
      const safeResult = result || {};
      this.setup = safeResult.setup || null;
      this.allowedActions = Array.isArray(safeResult.allowedActions)
        ? safeResult.allowedActions
        : [];
      this.allowedReasons = safeResult.allowedReasons || {};
      this.manualEnablementRequirements = safeResult.manualEnablementRequirements || null;
      if ("clientSSOProvider" in safeResult) {
        this.clientSSOProvider = safeResult.clientSSOProvider || null;
      }
      this.operationStatus = safeResult.operationStatus || "";
      if (!keepError) this.safeError = null;
      // A returned setup answers an unknown create outcome, so the old key and lock no longer apply.
      if (this.isCreateOutcomeUnknown && this.setup) this.resetForm();
      if (this.isTicketActionDialogOpen && !this.allowedActions.includes(this.ticketAction)) {
        this.closeTicketActionDialog();
      }
      if (this.isManualEnablementFormOpen && !this.canOpenManualEnablement) {
        this.closeManualEnablementForm();
      }
      this.syncTicketUrl();
    },
    issuanceKey(setup) {
      return setup ? `${setup.setupAttemptId}:${setup.ticketIssuanceVersion}` : "";
    },
    setTicketUrl(url, { copied = false } = {}) {
      this.transientTicketUrl = url || "";
      this.isTicketUrlCopied = Boolean(url) && copied;
      this.isCopyFailed = false;
      this.ticketUrlIssuance = url ? this.issuanceKey(this.setup) : "";
    },
    // The link is held in memory only, so drop it once it no longer belongs to the active ticket.
    syncTicketUrl() {
      if (
        this.transientTicketUrl &&
        (!this.isTicketCreated || this.ticketUrlIssuance !== this.issuanceKey(this.setup))
      ) {
        this.setTicketUrl("");
      }
    },
    async loadSetup({ keepError = false } = {}) {
      if (!this.isClientReady) return;
      const requestId = ++this.setupRequestId;
      const clientId = this.client.clientId;
      this.isLoadingSetup = true;
      this.setupLoadFailed = false;
      if (!keepError) this.safeError = null;
      try {
        const result = await AuthService.getClientSSOSetup(clientId);
        if (requestId !== this.setupRequestId) return;
        this.applySetupResult(result, { keepError });
      } catch (error) {
        if (requestId !== this.setupRequestId) return;
        this.setupLoadFailed = true;
        this.safeError = this.toSafeError(error);
      } finally {
        if (requestId === this.setupRequestId) this.isLoadingSetup = false;
      }
    },
    toSafeError(error) {
      const response = error && error.response;
      if (!response) {
        return { message: NETWORK_ERROR_MESSAGE, errorCode: "", code: "" };
      }
      const data = response.data || {};
      // `error` comes from the terraform-gcp error catalog; `message` is the TSOA 422 shape.
      const message =
        (typeof data.error === "string" && data.error) ||
        (typeof data.message === "string" && data.message) ||
        FALLBACK_ERROR_MESSAGE;
      return {
        message,
        errorCode: data.error_code ? String(data.error_code) : "",
        code: typeof data.code === "string" ? data.code : "",
        status: response.status,
      };
    },
    // Reuses the key only for a retry of the identical request.
    getIdempotencyKey(fingerprint) {
      if (!this.pendingRequest || this.pendingRequest.fingerprint !== fingerprint) {
        this.pendingRequest = { fingerprint, idempotencyKey: uuidv4() };
      }
      return this.pendingRequest.idempotencyKey;
    },
    // A retried mutation can answer 202/409 with a safe result instead of the error shape.
    async callSetupMutation(request) {
      try {
        return { result: (await request()) || {} };
      } catch (error) {
        const result = error && error.response && error.response.data && error.response.data.result;
        if (result) return { result };
        return { failure: this.toSafeError(error) };
      }
    },
    async applyMutationResult(result) {
      // An action result is newer than any reload already in flight, so those are discarded.
      this.setupRequestId += 1;
      this.isLoadingSetup = false;
      // Replays of in-progress or failed operations carry no setup, so read the current state.
      if (result.operationStatus || !result.setup) {
        this.setTicketUrl("");
        await this.loadSetup();
        if (!this.operationStatus) this.operationStatus = result.operationStatus || "";
        return;
      }
      this.applySetupResult(result);
      if (result.setupTicketUrl) this.setTicketUrl(result.setupTicketUrl);
      // Replays of completed operations omit reasons and requirements; reload to restore them.
      if (
        this.allowedActions.length > 0 &&
        !result.allowedReasons &&
        !result.manualEnablementRequirements
      ) {
        await this.loadSetup();
      }
    },
    async submit() {
      if (this.$refs.form) this.$refs.form.validate();
      if (!this.canCreate) return;

      const clientId = this.client.clientId;
      const epoch = this.clientEpoch;
      const payload = {
        clientAdminEmail: this.clientAdminEmail.trim(),
        clientAdminEmailConfirmation: this.clientAdminEmailConfirmation.trim(),
      };
      const idempotencyKey = this.getIdempotencyKey(
        `CREATE|${payload.clientAdminEmail}|${payload.clientAdminEmailConfirmation}`
      );

      this.isCreatingSetup = true;
      this.safeError = null;
      const { result, failure } = await this.callSetupMutation(() =>
        AuthService.createClientSSOSetup(clientId, payload, idempotencyKey)
      );
      if (epoch !== this.clientEpoch) return;
      this.isCreatingSetup = false;

      if (failure) {
        // Keep the key, and lock the inputs, only when the outcome is unknown.
        this.isCreateOutcomeUnknown = isOutcomeUnknown(failure);
        if (!this.isCreateOutcomeUnknown) this.pendingRequest = null;
        this.safeError = failure;
        if (failure.status === 409) await this.loadSetup({ keepError: true });
        return;
      }
      this.resetForm();
      await this.applyMutationResult(result);
    },
    async copyTicketUrl() {
      if (this.hasTicketExpired()) {
        this.expireTicketLink();
        return;
      }
      try {
        await navigator.clipboard.writeText(this.transientTicketUrl);
        this.isTicketUrlCopied = true;
        this.isCopyFailed = false;
      } catch (error) {
        this.isCopyFailed = true;
      }
    },
    dismissTicketUrl() {
      this.setTicketUrl("");
    },
    warnBeforeUnload(event) {
      if (!this.isLinkAtRisk) return;
      event.preventDefault();
      event.returnValue = "";
    },
    confirm({ title, message, confirmButtonText, cancelButtonText }) {
      this.resolveConfirmDialog(false);
      this.confirmDialog = { show: true, title, message, confirmButtonText, cancelButtonText };
      return new Promise((resolve) => {
        this.confirmDialogResolve = resolve;
      });
    },
    resolveConfirmDialog(confirmed) {
      const resolve = this.confirmDialogResolve;
      this.confirmDialogResolve = null;
      if (resolve) resolve(confirmed);
    },
    /** Public: called by DeveloperSettings before a tab change or route leave. */
    async confirmCanLeave() {
      if (!this.isLinkAtRisk) return true;
      if (!this.isFreshTicketUrl) {
        return this.confirm({
          title: "Setup link is being created",
          message:
            "If you leave now, the new setup link will not be shown and you will need to regenerate the ticket to get one. Leave anyway?",
          confirmButtonText: "Leave",
          cancelButtonText: "Stay",
        });
      }
      return this.confirm({
        title: "Setup link not copied",
        message:
          "The setup link is not saved anywhere. If you leave, you will need to regenerate the ticket to get a new link. Leave anyway?",
        confirmButtonText: "Leave",
        cancelButtonText: "Stay",
      });
    },
    hasTicketExpired() {
      return (
        Boolean(this.activeTicketExpiresAt) && Date.parse(this.activeTicketExpiresAt) <= Date.now()
      );
    },
    clearTicketExpiryTimer() {
      if (this.ticketExpiryTimer) clearTimeout(this.ticketExpiryTimer);
      this.ticketExpiryTimer = null;
    },
    // One timer per active ticket, not polling; the reload lets the backend mark it expired.
    scheduleTicketExpiry(expiresAt) {
      this.clearTicketExpiryTimer();
      const delay = expiresAt ? Date.parse(expiresAt) - Date.now() : NaN;
      if (Number.isNaN(delay)) return;
      this.ticketExpiryTimer = setTimeout(
        () => this.expireTicketLink(),
        Math.min(Math.max(delay, 0), MAX_TIMER_DELAY_MS)
      );
    },
    expireTicketLink() {
      this.clearTicketExpiryTimer();
      // Long delays are capped, so the timer can fire before the ticket actually expires.
      if (!this.hasTicketExpired()) {
        this.scheduleTicketExpiry(this.activeTicketExpiresAt);
        return;
      }
      this.dismissTicketUrl();
      this.loadSetup();
    },
    openTicketActionDialog(action) {
      if (!this.allowedActions.includes(action) || !this.setup) return;
      this.safeError = null;
      this.ticketAction = action;
      this.reasonCode = "";
      this.reasonNote = "";
      this.actionError = null;
      this.pendingRequest = null;
      this.isTicketActionDialogOpen = true;
    },
    closeTicketActionDialog() {
      if (this.isTicketActionPending) return;
      this.isTicketActionDialogOpen = false;
      this.isTicketActionOutcomeUnknown = false;
      this.ticketAction = "";
      this.reasonCode = "";
      this.reasonNote = "";
      this.actionError = null;
      this.pendingRequest = null;
    },
    // After an unknown outcome the action may have run, so show the real state before another try.
    cancelTicketActionDialog() {
      const outcomeUnknown = this.isTicketActionOutcomeUnknown;
      this.closeTicketActionDialog();
      if (outcomeUnknown) this.loadSetup();
    },
    async confirmTicketAction() {
      if (!this.canConfirmTicketAction) return;

      const action = this.ticketAction;
      const clientId = this.client.clientId;
      const setupAttemptId = this.setup.setupAttemptId;
      const epoch = this.clientEpoch;
      const note = this.reasonNote.trim();
      const payload = note
        ? { reasonCode: this.reasonCode, reasonNote: note }
        : { reasonCode: this.reasonCode };
      const idempotencyKey = this.getIdempotencyKey(
        `${action}|${setupAttemptId}|${payload.reasonCode}|${payload.reasonNote || ""}`
      );
      const request =
        action === "REVOKE_TICKET"
          ? AuthService.revokeClientSSOSetupTicket
          : AuthService.regenerateClientSSOSetupTicket;

      this.isTicketActionPending = true;
      this.actionError = null;
      const { result, failure } = await this.callSetupMutation(() =>
        request.call(AuthService, clientId, setupAttemptId, payload, idempotencyKey)
      );
      this.isTicketActionPending = false;
      if (epoch !== this.clientEpoch) return;

      if (failure && isOutcomeUnknown(failure)) {
        // Inputs lock so a retry sends the same key and the backend replays the original result.
        this.isTicketActionOutcomeUnknown = true;
        this.actionError = failure;
        return;
      }
      if (failure && failure.status === 400) {
        this.pendingRequest = null;
        this.isTicketActionOutcomeUnknown = false;
        this.actionError = failure;
        return;
      }

      this.closeTicketActionDialog();
      if (failure) {
        this.safeError = failure;
        await this.loadSetup({ keepError: true });
        return;
      }
      await this.applyMutationResult(result);
    },
    openManualEnablementForm() {
      if (!this.canOpenManualEnablement) return;
      this.safeError = null;
      this.acknowledgedRequirementIds = [];
      this.manualActivationReference = "";
      this.ssoButtonLabel = DEFAULT_SSO_BUTTON_LABEL;
      this.manualEnablementError = null;
      this.pendingRequest = null;
      this.isManualEnablementFormOpen = true;
    },
    closeManualEnablementForm() {
      if (this.isManualEnablementPending) return;
      this.isManualEnablementFormOpen = false;
      this.isManualEnablementConfirmationOpen = false;
      this.isManualEnablementOutcomeUnknown = false;
      this.acknowledgedRequirementIds = [];
      this.manualActivationReference = "";
      this.ssoButtonLabel = DEFAULT_SSO_BUTTON_LABEL;
      this.manualEnablementError = null;
      this.pendingRequest = null;
    },
    cancelManualEnablementForm() {
      const outcomeUnknown = this.isManualEnablementOutcomeUnknown;
      this.closeManualEnablementForm();
      if (outcomeUnknown) this.loadSetup();
    },
    cancelManualEnablementConfirmation() {
      if (this.isManualEnablementOutcomeUnknown) {
        this.cancelManualEnablementForm();
        return;
      }
      this.isManualEnablementConfirmationOpen = false;
    },
    openManualEnablementConfirmation() {
      if (!this.canSubmitManualEnablement) return;
      this.manualEnablementError = null;
      this.isManualEnablementConfirmationOpen = true;
    },
    async confirmManualEnablement() {
      if (!this.canSubmitManualEnablement) return;

      const clientId = this.client.clientId;
      const setupAttemptId = this.setup.setupAttemptId;
      const epoch = this.clientEpoch;
      const payload = {
        requirementsVersion: this.manualEnablementRequirements.version,
        acknowledgedRequirementIds: this.manualEnablementItems
          .map((requirement) => requirement.id)
          .filter((requirementId) => this.acknowledgedRequirementIds.includes(requirementId)),
        manualActivationReference: this.manualActivationReference.trim(),
        ssoButtonLabel: this.ssoButtonLabel.trim() || DEFAULT_SSO_BUTTON_LABEL,
      };
      const idempotencyKey = this.getIdempotencyKey(
        `MARK_MANUALLY_ENABLED|${setupAttemptId}|${
          payload.requirementsVersion
        }|${payload.acknowledgedRequirementIds.join(",")}|${payload.manualActivationReference}|${
          payload.ssoButtonLabel
        }`
      );

      this.isManualEnablementPending = true;
      this.manualEnablementError = null;
      const { result, failure } = await this.callSetupMutation(() =>
        AuthService.markClientSSOSetupManuallyEnabled(
          clientId,
          setupAttemptId,
          payload,
          idempotencyKey
        )
      );
      this.isManualEnablementPending = false;
      if (epoch !== this.clientEpoch) return;

      if (failure && isOutcomeUnknown(failure)) {
        // Inputs lock so a retry sends the same key and the backend replays the original result.
        this.isManualEnablementOutcomeUnknown = true;
        this.manualEnablementError = failure;
        return;
      }
      if (failure && failure.status === 400) {
        this.pendingRequest = null;
        this.isManualEnablementOutcomeUnknown = false;
        this.manualEnablementError = failure;
        this.isManualEnablementConfirmationOpen = false;
        return;
      }
      // The setup stays open, but the active ticket may now be revoked, so refresh behind the form.
      if (failure && failure.code === "SSO_SETUP_CLIENT_UPDATE_FAILED") {
        this.pendingRequest = null;
        this.isManualEnablementOutcomeUnknown = false;
        this.manualEnablementError = failure;
        this.isManualEnablementConfirmationOpen = false;
        await this.loadSetup({ keepError: true });
        // The reload closes the form when enablement is no longer allowed; keep the message visible.
        if (epoch === this.clientEpoch && !this.isManualEnablementFormOpen)
          this.safeError = failure;
        return;
      }

      this.closeManualEnablementForm();
      if (failure) {
        this.safeError = failure;
        await this.loadSetup({ keepError: true });
        return;
      }
      await this.applyMutationResult(result);
    },
  },
};
</script>

<style scoped>
.sso-setup-form {
  max-width: 560px;
}

.sso-ticket-created {
  max-width: 720px;
}

.sso-sensitive-link {
  border-color: #d7bb63 !important;
}

.sso-ticket-url {
  display: block;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.sso-ticket-actions {
  max-width: 420px;
}
</style>
